'use client';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId } from '../../utils';
import { Button } from '../../components/Button';

const paginationVariants = cva('flex items-center justify-between', {
  variants: {
    variant: {
      default: 'px-lg py-md border-t border-border-base',
      flush: 'mt-md'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

type PaginationVariantProps = VariantProps<typeof paginationVariants>;

type BasePaginationAttributes = Pick<React.HTMLAttributes<HTMLDivElement>, 'id' | 'className'>;

/** Labels for pagination UI elements. Pass translated strings from your app's i18n. */
export interface PaginationLabels {
  /** Summary text, e.g. "Page 1 of 3 (25 items)" */
  page: string;
  /** Previous button label */
  prev: string;
  /** Next button label */
  next: string;
}

export interface PaginationProps extends BasePaginationAttributes, PaginationVariantProps {
  /** Current page number (1-based) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Called when the user clicks the "previous" button */
  onPrev: () => void;
  /** Called when the user clicks the "next" button */
  onNext: () => void;
  /** Translated label strings */
  labels: PaginationLabels;
  /** Optional test ID for E2E selectors */
  testId?: string;
}

/**
 * Pagination — a simple prev/next pagination bar with page info summary.
 *
 * The component renders nothing when `totalPages <= 1`.
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   onPrev={prevPage}
 *   onNext={nextPage}
 *   labels={{
 *     page: t('pagination.page', { current: currentPage, total: totalPages, count: totalItems }),
 *     prev: t('pagination.prev'),
 *     next: t('pagination.next'),
 *   }}
 * />
 * ```
 */
export const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (
    { currentPage, totalPages, onPrev, onNext, labels, variant, className, testId, id, ...props },
    ref
  ) => {
    if (totalPages <= 1) return null;

    return (
      <div
        ref={ref}
        id={id}
        data-testid={getTestId({ testId, id })}
        className={cn(paginationVariants({ variant }), className)}
        {...props}
      >
        <span className='text-sm text-foreground-muted'>{labels.page}</span>
        <div className='flex gap-sm'>
          <Button
            variant='default'
            outline='default'
            size='sm'
            onClick={onPrev}
            disabled={currentPage <= 1}
            testId={testId ? `${testId}-prev` : undefined}
          >
            {labels.prev}
          </Button>
          <Button
            variant='default'
            outline='default'
            size='sm'
            onClick={onNext}
            disabled={currentPage >= totalPages}
            testId={testId ? `${testId}-next` : undefined}
          >
            {labels.next}
          </Button>
        </div>
      </div>
    );
  }
);

Pagination.displayName = 'Pagination';
